import { defineStore } from 'pinia'

export const usePersonalControlsStore = defineStore('personalControls', {
    state: () => ({
        personalControls: {},
        personalControlsInitialState: {},
        currentDeviceId: null,
        allowedEdits: false,
        availabilityCheckInterval: null
    }),

    actions: {
        setPersonalControls(personalControls) {
            this.personalControls = personalControls
        },

        setPersonalControlsInitialState(personalControls) {
            this.personalControlsInitialState = JSON.parse(JSON.stringify(personalControls))
        },

        setCurrentDeviceId(deviceId) {
            this.currentDeviceId = deviceId
        },

        /**
         * Represents a mapping of device identifiers to their respective personal controls rules.
         *
         * @type {Object.<string, Array<PersonalControlRule>>}
         *
         * Example structure:
         * {
         *   "device_id_1": [PersonalControlRule, PersonalControlRule, ...],
         *   "device_id_2": [PersonalControlRule, ...]
         * }
         */

        /**
         * @typedef {Object} PersonalControlRule
         * @property {number} id - Unique rule identifier
         * @property {string} ruleName - Human-readable label for the rule
         * @property {Array<number>} weekDays - Days of the week rule applies (1=Monday ... 7=Sunday)
         * @property {string} startTime - Start time for portal access (format: 'HH:mm')
         * @property {string} endTime - End time for portal access (format: 'HH:mm')
         * @property {boolean} active - Whether this rule is currently active
         * @property {string} [description] - Optional description of the rule
         */

        getPersonalControls() {
            return this.personalControls
        },

        getPersonalControlsInitialState() {
            return this.personalControlsInitialState
        },

        getPersonalControlsForDevice(deviceId) {
            return this.personalControls[deviceId] || []
        },

        /**
         * Check if current time is within any Personal control window
         * Uses client's local timezone for accurate time checking
         * 
         * @param {Array} personalControls - Array of personal control rules
         * @returns {boolean} - true if currently in allowed window (edits allowed), false if restricted
         */
        isCurrentlyAvailableForEditing(personalControls) {
            if (!personalControls || !Array.isArray(personalControls) || personalControls.length === 0) {
                return true; // No personal controls = always editable
            }

            const now = new Date();
            const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
            const currentTime = now.toTimeString().slice(0, 5);

            // Convert PHP weekday format (1=Monday, 7=Sunday) to JavaScript format (0=Sunday, 1=Monday)
            const phpToJsDay = (phpDay) => phpDay === 7 ? 0 : phpDay;

            for (const rule of personalControls) {
                // Check if current day matches any of the rule's weekdays
                if (rule.weekDays && rule.weekDays.some(day => phpToJsDay(day) === currentDay)) {
                    // Simple time comparison using minutes since midnight
                    const timeToMinutes = (timeStr) => {
                        const [hours, minutes] = timeStr.split(':').map(Number);
                        return hours * 60 + minutes;
                    };

                    const currentMinutes = timeToMinutes(currentTime);
                    const startMinutes = timeToMinutes(rule.startTime);
                    const endMinutes = timeToMinutes(rule.endTime);
                    
                    // Normal time range within same day
                    if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
                        return true; // Currently in allowed window
                    }
                }
            }

            return false; // Not in any allowed window
        },

        getAllowedEdits() {
            return this.allowedEdits
        },

        /**
         * Start periodic checking of availability for editing
         * Checks every minute to see if time windows have opened or closed
         */
        startAvailabilityCheck() {
            this.stopAvailabilityCheck();

            this.updateAvailabilityStatus();

            this.availabilityCheckInterval = setInterval(() => {
                this.updateAvailabilityStatus();
            }, 60000);
        },

        /**
         * Stop the periodic availability checking
         */
        stopAvailabilityCheck() {
            if (this.availabilityCheckInterval) {
                clearInterval(this.availabilityCheckInterval);
                this.availabilityCheckInterval = null;
            }
        },

        /**
         * Update the allowedEdits status based on current time
         * This method checks if we're currently in an allowed time window
         */
        updateAvailabilityStatus() {
            const personalControls = this.getPersonalControls();
            const canEdit = this.isCurrentlyAvailableForEditing(personalControls);
            
            if (this.allowedEdits !== canEdit) {
                this.allowedEdits = canEdit;
            }
        }
    }
})